/**
 * utils/assetClass.ts
 * Pure utility functions for AssetClass domain
 *
 * Parametric by design: all functions accept data maps as parameters rather than
 * importing from `constants/assetClass`. This maintains utils purity per CODING_STANDARDS
 * and ensures functions remain testable in isolation.
 *
 * USAGE:
 * - Import display/validation maps from `constants/assetClass` at call sites
 * - Pass maps as function arguments
 * - Functions are pure: same inputs always produce same outputs
 *
 * @example
 * ```ts
 * // Import maps from constants (at call site, not in this file)
 * import {
 *   ASSET_CLASS_TYPE_DISPLAY_MAP,
 *   VALID_MARKET_SEGMENTS_BY_ASSET_CLASS
 * } from '@/constants/assetClass';
 * import { formatAssetClassTypeDisplay, isValidMarketSegmentForAssetClass } from '@/utils/assetClass';
 *
 * // Pass maps as arguments (parametric pattern)
 * const display = formatAssetClassTypeDisplay('futures', ASSET_CLASS_TYPE_DISPLAY_MAP);
 * const isValid = isValidMarketSegmentForAssetClass('futures', 'indices', VALID_MARKET_SEGMENTS_BY_ASSET_CLASS);
 * ```
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero imports from `constants/` or other domain modules
 * - Only `import type` from `types/` (no runtime dependencies)
 * - No side effects (no logs, no mutations, no I/O, no caches)
 * - All functions are deterministic and testable
 * - **This file must not import constants** (parametric design requirement)
 */

// Internal absolute imports (type-only)
import type {
  AssetClass,
  AssetClassType,
  MarketSegment,
  SubAssetClass
} from '@/types/shared/AssetClass';

/**
 * Hard-coded SubAssetClass membership list (79 values)
 * Readonly tuple to avoid type widening
 */
const SUB_ASSET_CLASS_VALUES = [
  'adr', 'aggregate', 'ai-token', 'all-world', 'broad-market', 'brazil',
  'china', 'cross-pair', 'crude-oil', 'defi', 'developed-ex-us',
  'developed-markets', 'dividend', 'dividend-growth', 'em', 'em-all-cap',
  'em-large-mid', 'em-thematic', 'emerging-markets', 'energy', 'enterprise',
  'equity-options', 'etf', 'etf-options', 'europe', 'exchange-token',
  'factor', 'floating-rate', 'frontier-blend', 'futures-options', 'g10',
  'gaming', 'general-obligation', 'high-yield', 'index-options', 'industrial',
  'infrastructure', 'investment-grade', 'japan', 'large-cap-blend',
  'large-cap-growth', 'large-cap-value', 'layer-0', 'layer-1', 'layer-2',
  'mbs', 'meme', 'meme-coin', 'mexico', 'micro', 'mid-cap-blend', 'mini',
  'minimum-volatility', 'momentum', 'mortgage-backed', 'municipal', 'nano',
  'natural-gas', 'oracle', 'other', 'payment-token', 'payments', 'precious',
  'privacy', 'quality', 'regional-equities', 'sector', 'single_stock', 'size',
  'small-cap-blend', 'south-africa', 'stablecoin', 'standard', 'stock',
  'stock-index', 'stock_index', 'tips', 'total-ex-us', 'total-market',
  'treasury', 'value'
] as const;

/**
 * Type guard to check if an asset classification has a sub-asset class
 *
 * Uses runtime string check + hard-coded membership list for exhaustive validation.
 * Case-sensitive matching: expects exact `SubAssetClass` literals; no normalization performed.
 *
 * @param value - Unknown value to check
 * @returns true if value is a valid SubAssetClass string
 *
 * @example
 * ```ts
 * hasSubAssetClass('micro')  // => true
 * hasSubAssetClass('Micro')  // => false (case-sensitive)
 * hasSubAssetClass(null)     // => false
 * ```
 */
export function hasSubAssetClass(value: unknown): value is SubAssetClass {
  return (
    typeof value === 'string' &&
    (SUB_ASSET_CLASS_VALUES as readonly string[]).includes(value)
  );
}

/**
 * Format asset class type for display
 *
 * Parametric formatter: accepts display map as argument to maintain purity.
 * Falls back to raw value if key missing in map (allows UI to handle unknown values).
 *
 * @param value - Asset class type to format
 * @param displayMap - Record mapping AssetClassType to display strings
 * @returns Formatted display string (or raw value if not in map)
 *
 * @example
 * ```ts
 * import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
 * const display = formatAssetClassTypeDisplay('futures', ASSET_CLASS_TYPE_DISPLAY_MAP);
 * // => 'Futures'
 * ```
 */
export function formatAssetClassTypeDisplay(
  value: AssetClassType,
  displayMap: Readonly<Record<AssetClassType, string>>
): string {
  return displayMap[value] ?? value;
}

/**
 * Format market segment for display
 *
 * Parametric formatter: accepts display map as argument to maintain purity.
 * Falls back to raw value if key missing in map (allows UI to handle unknown values).
 *
 * @param value - Market segment to format
 * @param displayMap - Record mapping MarketSegment to display strings
 * @returns Formatted display string (or raw value if not in map)
 *
 * @example
 * ```ts
 * import { MARKET_SEGMENT_DISPLAY_MAP } from '@/constants/assetClass';
 * const display = formatMarketSegmentDisplay('us-equities', MARKET_SEGMENT_DISPLAY_MAP);
 * // => 'US Equities'
 * ```
 */
export function formatMarketSegmentDisplay(
  value: MarketSegment,
  displayMap: Readonly<Record<MarketSegment, string>>
): string {
  return displayMap[value] ?? value;
}

/**
 * Format sub-asset class for display
 *
 * Parametric formatter: accepts display map as argument to maintain purity.
 * Falls back to raw value if key missing in map (allows UI to handle unknown values).
 *
 * @param value - Sub-asset class to format
 * @param displayMap - Record mapping SubAssetClass to display strings
 * @returns Formatted display string (or raw value if not in map)
 *
 * @example
 * ```ts
 * import { SUB_ASSET_CLASS_DISPLAY_MAP } from '@/constants/assetClass';
 * const display = formatSubAssetClassDisplay('micro', SUB_ASSET_CLASS_DISPLAY_MAP);
 * // => 'Micro'
 * ```
 */
export function formatSubAssetClassDisplay(
  value: SubAssetClass,
  displayMap: Readonly<Record<SubAssetClass, string>>
): string {
  return displayMap[value] ?? value;
}

/**
 * Format complete AssetClass object for display
 *
 * Parametric formatter: accepts all three display maps as arguments.
 *
 * Output formats:
 * - With subAssetClass: `"Type - Segment (Sub)"`
 * - Without subAssetClass: `"Type - Segment"`
 *
 * @param assetClass - Complete asset classification object
 * @param typeMap - Display map for asset class types
 * @param segmentMap - Display map for market segments
 * @param subAssetMap - Display map for sub-asset classes
 * @returns Formatted display string (whitespace normalized)
 *
 * @example
 * ```ts
 * import {
 *   ASSET_CLASS_TYPE_DISPLAY_MAP,
 *   MARKET_SEGMENT_DISPLAY_MAP,
 *   SUB_ASSET_CLASS_DISPLAY_MAP
 * } from '@/constants/assetClass';
 *
 * formatAssetClassDisplay(
 *   { assetClass: 'futures', marketSegment: 'indices', subAssetClass: 'micro' },
 *   ASSET_CLASS_TYPE_DISPLAY_MAP,
 *   MARKET_SEGMENT_DISPLAY_MAP,
 *   SUB_ASSET_CLASS_DISPLAY_MAP
 * );
 * // => 'Futures - Indices (Micro)'
 *
 * formatAssetClassDisplay(
 *   { assetClass: 'forex', marketSegment: 'majors' },
 *   ASSET_CLASS_TYPE_DISPLAY_MAP,
 *   MARKET_SEGMENT_DISPLAY_MAP,
 *   SUB_ASSET_CLASS_DISPLAY_MAP
 * );
 * // => 'Forex - Major Pairs'
 * ```
 */
export function formatAssetClassDisplay(
  assetClass: AssetClass,
  typeMap: Readonly<Record<AssetClassType, string>>,
  segmentMap: Readonly<Record<MarketSegment, string>>,
  subAssetMap: Readonly<Record<SubAssetClass, string>>
): string {
  const typeDisplay = formatAssetClassTypeDisplay(assetClass.assetClass, typeMap);
  const segmentDisplay = formatMarketSegmentDisplay(assetClass.marketSegment, segmentMap);
  let result = `${typeDisplay} - ${segmentDisplay}`;

  // Handle subAssetClass (guards against both undefined and null)
  if (assetClass.subAssetClass != null) {
    const subAssetDisplay = formatSubAssetClassDisplay(assetClass.subAssetClass, subAssetMap);
    result = `${result} (${subAssetDisplay})`;
  }

  // Collapse multiple spaces and trim (guards against malformed map values)
  return result.replace(/\s+/g, ' ').trim();
}

/**
 * Create asset classification identifier string
 *
 * Pure ID generator with deterministic canonicalization:
 * 1. Concatenate: `${type}__${segment}__${sub ?? 'none'}`
 * 2. Lowercase entire string
 * 3. Trim whitespace
 * 4. Replace sequences of non-alphanumerics with single hyphen
 * 5. Collapse multiple hyphens
 * 6. Trim leading/trailing hyphens
 *
 * No locale transforms (uses basic ASCII normalization for SSR/client consistency).
 * Assumes inputs are valid `AssetClass` domain literals, not free text.
 * Any upstream free-text coercion should happen before calling this function.
 *
 * @param assetClass - Complete asset classification object (domain literals)
 * @returns Canonicalized identifier string
 *
 * @example
 * ```ts
 * createAssetClassId({
 *   assetClass: 'futures',
 *   marketSegment: 'indices',
 *   subAssetClass: 'micro'
 * });
 * // => 'futures__indices__micro'
 *
 * createAssetClassId({
 *   assetClass: 'forex',
 *   marketSegment: 'majors'
 * });
 * // => 'forex__majors__none'
 * ```
 */
export function createAssetClassId(assetClass: AssetClass): string {
  // Step 1: Concatenate with double-underscore separators
  const raw = `${assetClass.assetClass}__${assetClass.marketSegment}__${assetClass.subAssetClass ?? 'none'}`;

  // Step 2-6: Normalize (lowercase, trim, replace non-alphanumerics, collapse/trim hyphens)
  return raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]+/g, '-')  // Replace non-alphanumerics with hyphen
    .replace(/-+/g, '-')             // Collapse multiple hyphens
    .replace(/^-|-$/g, '');          // Trim leading/trailing hyphens
}

/**
 * Validate if market segment is valid for the given asset class
 *
 * Parametric validator: accepts validation map as argument to maintain purity.
 * Returns false for unknown asset class types (defensive behavior, no throw).
 *
 * @param assetClassType - Asset class type to check
 * @param marketSegment - Market segment to validate
 * @param validationMap - Record mapping AssetClassType to allowed MarketSegment arrays
 * @returns true if segment is valid for asset class, false otherwise
 *
 * @example
 * ```ts
 * import { VALID_MARKET_SEGMENTS_BY_ASSET_CLASS } from '@/constants/assetClass';
 *
 * const isValid = isValidMarketSegmentForAssetClass(
 *   'futures',
 *   'indices',
 *   VALID_MARKET_SEGMENTS_BY_ASSET_CLASS
 * );
 * // => true
 *
 * const isInvalid = isValidMarketSegmentForAssetClass(
 *   'futures',
 *   'digital-assets',
 *   VALID_MARKET_SEGMENTS_BY_ASSET_CLASS
 * );
 * // => false
 * ```
 */
export function isValidMarketSegmentForAssetClass(
  assetClassType: AssetClassType,
  marketSegment: MarketSegment,
  validationMap: Readonly<Record<AssetClassType, readonly MarketSegment[]>>
): boolean {
  // Early return: unknown asset class type (defensive against prototype pollution)
  if (!Object.prototype.hasOwnProperty.call(validationMap, assetClassType)) {
    return false;
  }
  return validationMap[assetClassType].includes(marketSegment);
}

/**
 * Validate if sub-asset class is valid for the given asset class
 *
 * Parametric validator: accepts validation map as argument to maintain purity.
 * Returns false for unknown asset class types (defensive behavior, no throw).
 *
 * @param assetClassType - Asset class type to check
 * @param subAssetClass - Sub-asset class to validate
 * @param validationMap - Record mapping AssetClassType to allowed SubAssetClass arrays
 * @returns true if sub-asset class is valid for asset class, false otherwise
 *
 * @example
 * ```ts
 * import { VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS } from '@/constants/assetClass';
 *
 * const isValid = isValidSubAssetClassForAssetClass(
 *   'futures',
 *   'micro',
 *   VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS
 * );
 * // => true
 *
 * const isInvalid = isValidSubAssetClassForAssetClass(
 *   'futures',
 *   'stablecoin',
 *   VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS
 * );
 * // => false
 * ```
 */
export function isValidSubAssetClassForAssetClass(
  assetClassType: AssetClassType,
  subAssetClass: SubAssetClass,
  validationMap: Readonly<Record<AssetClassType, readonly SubAssetClass[]>>
): boolean {
  // Early return: unknown asset class type (defensive against prototype pollution)
  if (!Object.prototype.hasOwnProperty.call(validationMap, assetClassType)) {
    return false;
  }
  return validationMap[assetClassType].includes(subAssetClass);
}
